import {
	Dispatch,
	FC,
	SetStateAction,
	useCallback,
	useEffect,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { CheckmarkIcon, CrossIcon } from '../icons';
import DotIcon from '../icons/dot.svg';

import { PasswordDescription } from './PasswordValidationConditions.styles';

interface ICheckmarks {
	specialCharacter: boolean;
	capital: boolean;
	lowercase: boolean;
	length: boolean;
	noSpaces: boolean;
}

const defaultConditions: ICheckmarks = {
	specialCharacter: false,
	capital: false,
	lowercase: false,
	length: false,
	noSpaces: false,
};

interface PasswordValidationConditionsProps {
	password: string;
	fieldTouched?: boolean;
	checkPasswordErrors?: boolean;
	driverApp?: boolean;
	setPasswordIsValid?: Dispatch<SetStateAction<boolean>>;
}

export const PasswordValidationConditions: FC<
	PasswordValidationConditionsProps
> = ({
	password,
	checkPasswordErrors,
	driverApp,
	setPasswordIsValid,
	fieldTouched,
}) => {
	const { t } = useTranslation(['registration', 'validation']);

	const [checkmarkObj, setCheckmarkObj] =
		useState<ICheckmarks>(defaultConditions);
	const [conditionErrorsObj, setConditionErrorsObj] =
		useState<ICheckmarks>(defaultConditions);

	const checkMarksConditions = useCallback(() => {
		setCheckmarkObj({
			specialCharacter: Boolean(
				password.match(/^(?=.*[!@#$%^&*?(){}[\]/|=~\-+.,_><:])/)
			),
			capital: Boolean(password.match(/^(?=.*[A-ZÄÖÜẞ])/)),
			lowercase: Boolean(password.match(/^(?=.*[a-zäöüß])/)),
			length: Boolean(password.match(/^.{8,30}$/)),
			noSpaces: Boolean(!password.match(/\s/)),
		});
	}, [password]);

	const checkErrorsConditions = useCallback(() => {
		setConditionErrorsObj({
			specialCharacter: !checkmarkObj.specialCharacter,
			capital: !checkmarkObj.capital,
			lowercase: !checkmarkObj.lowercase,
			length: !checkmarkObj.length,
			noSpaces: !checkmarkObj.noSpaces,
		});
	}, [checkmarkObj]);

	useEffect(() => {
		checkMarksConditions();
	}, [password, checkMarksConditions]);

	useEffect(() => {
		if (checkPasswordErrors) {
			checkErrorsConditions();
		}
	}, [checkPasswordErrors, checkErrorsConditions]);

	useEffect(() => {
		if (setPasswordIsValid) {
			setPasswordIsValid(Object.values(checkmarkObj).every((value) => value));
		}
	}, [checkmarkObj, setPasswordIsValid]);

	const getRuleIcon = (ruleKey: keyof ICheckmarks) => {
		if (checkmarkObj[ruleKey]) {
			return <CheckmarkIcon />;
		}

		if (conditionErrorsObj[ruleKey] && fieldTouched) {
			return <CrossIcon />;
		}

		return <DotIcon />;
	};

	return (
		<Stack gap={1}>
			<PasswordDescription
				error={
					conditionErrorsObj.specialCharacter && !checkmarkObj.specialCharacter
				}
				data-testid="registrationForm-password-validation-rule-specialCharacter"
				driverApp={driverApp}
				success={checkmarkObj.specialCharacter}
			>
				{getRuleIcon('specialCharacter')}
				<Typography variant="b2">
					{t('validation:password.special_character')}
				</Typography>
			</PasswordDescription>
			<PasswordDescription
				error={conditionErrorsObj.capital && !checkmarkObj.capital}
				data-testid="registrationForm-password-validation-rule-capital"
				driverApp={driverApp}
				success={checkmarkObj.capital}
			>
				{getRuleIcon('capital')}
				<Typography variant="b2">{t('validation:password.capital')}</Typography>
			</PasswordDescription>
			<PasswordDescription
				error={conditionErrorsObj.lowercase && !checkmarkObj.lowercase}
				data-testid="registrationForm-password-validation-rule-lowercase"
				driverApp={driverApp}
				success={checkmarkObj.lowercase}
			>
				{getRuleIcon('lowercase')}
				<Typography variant="b2">
					{t('validation:password.lowercase')}
				</Typography>
			</PasswordDescription>
			<PasswordDescription
				error={conditionErrorsObj.length && !checkmarkObj.length}
				data-testid="registrationForm-password-validation-rule-length"
				driverApp={driverApp}
				success={checkmarkObj.length}
			>
				{getRuleIcon('length')}
				<Typography variant="b2">{t('validation:password.length')}</Typography>
			</PasswordDescription>
		</Stack>
	);
};
